<template>
  <!-- Treasure Hall Modal -->
  <div v-if="gameState.inTreasureHall" class="treasure-modal" @click="closeModal">
    <div class="treasure-card" @click.stop>
      <!-- Fixed Header -->
      <div class="treasure-header">
        <h3>聚宝阁</h3>
        <p>神秘的宝阁，汇聚天下珍宝，你可以用灵石购买各种秘籍和宝物。</p>
      </div>

      <!-- Category Tabs -->
      <div class="treasure-tabs">
        <button
          v-for="category in categories"
          :key="category.id"
          @click="activeCategory = category.id"
          :class="['tab-btn', { active: activeCategory === category.id }]"
        >
          {{ category.name }} ({{ category.count }})
        </button>
      </div>

      <!-- Scrollable Content -->
      <div class="treasure-content">
        <!-- Shop Items -->
        <div class="shop-section">
          <h4>{{ currentCategoryName }}商品列表</h4>
          <div class="shop-items">
            <div
              v-for="item in filteredShopItems"
              :key="item.id"
              class="shop-item"
              :class="{ 'unlocked': item.unlocked }"
            >
              <div class="item-main">
                <div class="item-name">{{ item.name }}</div>
                <div class="item-meta">
                  <div class="item-rarity" :class="`rarity-${item.rarity}`">{{ item.rarityText }}</div>
                  <div class="item-type">{{ item.type }}</div>
                </div>
                <div class="item-description">{{ item.description }}</div>
              </div>
              <div class="item-price-actions">
                <div class="item-price">{{ item.price }} 灵石</div>
                <div class="item-actions">
                  <button
                    v-if="!item.unlocked"
                    @click="purchaseItem(item)"
                    class="purchase-btn"
                    :disabled="!canAfford(item.price)"
                  >
                    购买
                  </button>
                  <div v-else class="unlocked-badge">已拥有</div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="filteredShopItems.length === 0" class="empty-category">
            <p>{{ currentCategoryName }}分类暂无商品</p>
          </div>
        </div>

      </div>

      <!-- Purchase Confirmation Modal -->
      <div v-if="showConfirmDialog" class="confirm-modal" @click="cancelPurchase">
        <div class="confirm-card" @click.stop>
          <h4>确认购买</h4>
          <div class="confirm-content">
            <p>确定要购买 <strong>{{ pendingPurchase?.name }}</strong> 吗？</p>
            <div class="purchase-details">
              <div class="detail-item">
                <span class="detail-label">价格:</span>
                <span class="detail-value">{{ pendingPurchase?.price }} 灵石</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">当前灵石:</span>
                <span class="detail-value">{{ playerSpiritStones }}</span>
              </div>
            </div>
          </div>
          <div class="confirm-actions">
            <button @click="cancelPurchase" class="cancel-btn">取消</button>
            <button @click="confirmPurchase" class="confirm-btn">确认购买</button>
          </div>
        </div>
      </div>

      <!-- Fixed Footer -->
      <div class="treasure-footer">
        <div class="player-info">
          你的灵石: {{ playerSpiritStones }}
        </div>
        <button @click="closeModal" class="close-btn">离开</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getRefinablePills, getRefinableTreasures, ITEMS } from '../resources.js';

export default {
  name: 'TreasureHallModal',
  props: {
    gameState: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      shopItems: [],
      activeCategory: 'all',
      showConfirmDialog: false,
      pendingPurchase: null
    };
  },
  mounted() {
    this.initializeShop();
  },
  computed: {
    playerSpiritStones() {
      return this.gameState.bag['灵石'] || 0;
    },

    // 分类列表
    categories() {
      const cats = [
        { id: 'all', name: '全部', count: this.shopItems.length },
        { id: 'pill', name: '丹方', count: this.shopItems.filter(item => item.itemType === 'pill').length },
        { id: 'treasure', name: '器方', count: this.shopItems.filter(item => item.itemType === 'treasure').length }
      ];
      return cats;
    },

    // 过滤后的商品列表
    filteredShopItems() {
      if (this.activeCategory === 'all') {
        return this.shopItems;
      }
      return this.shopItems.filter(item => item.itemType === this.activeCategory);
    },

    // 当前分类名称
    currentCategoryName() {
      const category = this.categories.find(cat => cat.id === this.activeCategory);
      return category ? category.name : '全部';
    }
  },
  methods: {
    // 初始化商店商品
    initializeShop() {
      // 丹方商品
      const pillRecipes = getRefinablePills();
      const pillItems = pillRecipes.map(pillName => {
        const item = ITEMS[pillName];
        return {
          id: `pill_${pillName}`,
          name: pillName,
          description: item.description,
          price: this.calculatePrice(item),
          type: '丹方',
          itemType: 'pill',
          rarity: item.rarity || 1,
          rarityText: this.getRarityText(item.rarity || 1),
          unlocked: this.isPillUnlocked(pillName)
        };
      });

      // 器方商品 (暂时为空，后续扩展)
      const treasureItems = []; // getRefinableTreasures().map(treasureName => ({...}))

      this.shopItems = [...pillItems, ...treasureItems];
    },

    // 获取稀有度文本
    getRarityText(rarity) {
      const rarityMap = {
        1: '普通',
        2: '优秀',
        3: '珍贵',
        4: '稀有',
        5: '传说',
        6: '史诗',
        7: '神话',
        8: '至尊'
      };
      return rarityMap[rarity] || '普通';
    },

    // 计算商品价格
    calculatePrice(item) {
      // 基础价格根据稀有度和难度计算
      const basePrice = item.rarity * 50 + (item.recipe?.difficulty || 1) * 20;
      return Math.max(basePrice, 100); // 最低100灵石
    },

    // 检查丹方是否已解锁
    isPillUnlocked(pillName) {
      return this.gameState.unlockedPills && this.gameState.unlockedPills.includes(pillName);
    },

    // 检查是否能购买
    canAfford(price) {
      return this.playerSpiritStones >= price;
    },

    // 购买商品 - 显示确认对话框
    purchaseItem(item) {
      if (!this.canAfford(item.price)) {
        // 显示错误toast
        this.$emit('purchase-result', '灵石不足，无法购买！');
        return;
      }

      // 显示确认对话框
      this.pendingPurchase = item;
      this.showConfirmDialog = true;
    },

    // 确认购买
    confirmPurchase() {
      const item = this.pendingPurchase;
      if (!item) return;

      // 扣除灵石
      this.gameState.bag['灵石'] -= item.price;
      if (this.gameState.bag['灵石'] <= 0) {
        delete this.gameState.bag['灵石'];
      }

      // 解锁丹方
      if (!this.gameState.unlockedPills) {
        this.gameState.unlockedPills = [];
      }
      if (!this.gameState.unlockedPills.includes(item.name)) {
        this.gameState.unlockedPills.push(item.name);
      }

      // 添加到背包作为可使用物品
      const itemName = `${item.name}丹方`;
      if (this.gameState.bag[itemName]) {
        this.gameState.bag[itemName] += 1;
      } else {
        this.gameState.bag[itemName] = 1;
      }

      // 保存状态
      this.$emit('save-state');

      // 关闭确认对话框
      this.showConfirmDialog = false;
      this.pendingPurchase = null;

      // 发送toast通知
      this.$emit('purchase-result', `成功购买【${item.name}丹方】！`);
    },

    // 取消购买
    cancelPurchase() {
      this.showConfirmDialog = false;
      this.pendingPurchase = null;
    },

    // 关闭模态框
    closeModal() {
      this.gameState.inTreasureHall = false;
    }
  }
};
</script>

<style scoped>
.treasure-modal {
  position: absolute;
  inset: 0px 0px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.8);
  z-index: 250;
  backdrop-filter: blur(2px);
}

.treasure-card {
  background: #071328;
  border-radius: 12px;
  border: 1px solid #334155;
  color: #e6eef8;
  min-width: 500px;
  max-width: 700px;
  height: 600px;
  display: flex;
  flex-direction: column;
}

.treasure-header {
  flex-shrink: 0;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #334155;
}

.treasure-tabs {
  flex-shrink: 0;
  padding: 12px 20px;
  border-bottom: 1px solid #334155;
  display: flex;
  gap: 8px;
  overflow-x: auto;
}

.tab-btn {
  padding: 6px 12px;
  border: 1px solid #475569;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  transition: all 0.2s;
  min-width: 60px;
}

.tab-btn:hover {
  background: rgba(96, 165, 250, 0.1);
  border-color: #60a5fa;
}

.tab-btn.active {
  background: #60a5fa;
  color: white;
  border-color: #60a5fa;
}

.treasure-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.treasure-footer {
  flex-shrink: 0;
  padding: 16px 20px 20px 20px;
  border-top: 1px solid #334155;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.treasure-card h3 {
  margin: 0;
  font-size: 18px;
  color: #60a5fa;
  text-align: center;
  border-bottom: 1px solid #334155;
  padding-bottom: 8px;
}

.treasure-card p {
  margin: 0;
  color: #cbd5e1;
  font-size: 14px;
  text-align: center;
  margin-top: 8px;
}

.shop-section h4 {
  margin: 8px 0 12px 0;
  font-size: 16px;
  color: #34d399;
}

.shop-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shop-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s;
  min-height: 60px;
}

.shop-item:hover {
  background: rgba(96, 165, 250, 0.05);
  border-color: #60a5fa;
}

.shop-item.unlocked {
  background: rgba(52, 211, 153, 0.05);
  border-color: #34d399;
}

.item-main {
  flex: 1;
  min-width: 0;
  margin-right: 12px;
}

.item-name {
  font-weight: bold;
  color: #dbeafe;
  font-size: 14px;
  margin-bottom: 4px;
}

.item-price-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.item-price {
  color: #fbbf24;
  font-size: 12px;
  font-weight: bold;
  text-align: right;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}

.item-rarity {
  font-size: 10px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 3px;
  display: inline-block;
}

.item-type {
  color: #a78bfa;
  font-size: 10px;
  font-style: italic;
}

.item-description {
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
}

.rarity-1 { background: #6b7280; color: white; } /* 普通 - 灰色 */
.rarity-2 { background: #10b981; color: white; } /* 优秀 - 绿色 */
.rarity-3 { background: #3b82f6; color: white; } /* 珍贵 - 蓝色 */
.rarity-4 { background: #8b5cf6; color: white; } /* 稀有 - 紫色 */
.rarity-5 { background: #f59e0b; color: white; } /* 传说 - 橙色 */
.rarity-6 { background: #ef4444; color: white; } /* 史诗 - 红色 */
.rarity-7 { background: #dc2626; color: white; } /* 神话 - 深红 */
.rarity-8 { background: linear-gradient(45deg, #fbbf24, #f59e0b); color: black; font-weight: bold; } /* 至尊 - 金色渐变 */

.item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.purchase-btn {
  padding: 6px 12px;
  border: 1px solid #16a34a;
  background: transparent;
  color: #16a34a;
  cursor: pointer;
  font-size: 13px;
  border-radius: 6px;
  transition: all 0.2s;
}

.purchase-btn:hover:not(:disabled) {
  background: #16a34a;
  color: white;
}

.purchase-btn:disabled {
  border-color: #475569;
  color: #64748b;
  cursor: not-allowed;
}

.unlocked-badge {
  padding: 4px 8px;
  background: #34d399;
  color: white;
  font-size: 12px;
  border-radius: 4px;
  font-weight: bold;
}

.status-message {
  margin-top: 12px;
  padding: 8px 12px;
  background: #1e293b;
  border: 1px solid #334155;
  font-size: 13px;
  color: #ffd166;
  border-radius: 6px;
  text-align: center;
}

.player-info {
  color: #fbbf24;
  font-size: 14px;
  font-weight: bold;
}

.close-btn {
  padding: 8px 16px;
  border: 1px solid #dc2626;
  background: transparent;
  color: #dc2626;
  cursor: pointer;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #dc2626;
  color: white;
}

.empty-category {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-size: 14px;
}

.empty-category p {
  margin: 0;
  color: #64748b;
}

/* 适配移动设备 */
@media (max-width: 900px) {
  .treasure-card {
    min-width: 95vw;
    max-width: 95vw;
    height: 80vh;
  }

  .treasure-header {
    padding: 12px 12px 8px 12px;
  }

  .treasure-header h3 {
    font-size: 16px;
  }

  .treasure-header p {
    font-size: 12px;
  }

  .treasure-tabs {
    padding: 8px 12px;
  }

  .tab-btn {
    padding: 4px 8px;
    font-size: 11px;
    min-width: 50px;
  }

  .treasure-content {
    padding: 8px 12px;
  }

  .shop-section h4 {
    font-size: 14px;
    margin: 6px 0 8px 0;
  }

  .shop-items {
    gap: 6px;
  }

  .shop-item {
    padding: 6px 8px;
    min-height: 50px;
  }

  .item-main {
    margin-right: 8px;
  }

  .item-name {
    font-size: 13px;
    margin-bottom: 2px;
  }

  .item-price-actions {
    gap: 2px;
  }

  .item-price {
    font-size: 11px;
  }

  .item-meta {
    margin-bottom: 1px;
  }

  .item-rarity {
    font-size: 9px;
    padding: 1px 3px;
  }

  .item-type {
    font-size: 9px;
  }

  .item-description {
    font-size: 10px;
    line-height: 1.2;
  }

  .purchase-btn {
    padding: 4px 8px;
    font-size: 11px;
  }

  .unlocked-badge {
    padding: 3px 6px;
    font-size: 10px;
  }

  .treasure-footer {
    padding: 8px 12px 12px 12px;
    flex-direction: column;
    gap: 8px;
  }

  .player-info {
    font-size: 12px;
  }

  .close-btn {
    padding: 6px 12px;
    font-size: 12px;
  }
}

/* 确认购买对话框 */
.confirm-modal {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 300;
}

.confirm-card {
  background: #071328;
  border-radius: 8px;
  border: 1px solid #334155;
  padding: 20px;
  min-width: 300px;
  max-width: 400px;
}

.confirm-card h4 {
  margin: 0 0 16px 0;
  color: #60a5fa;
  text-align: center;
  font-size: 16px;
}

.confirm-content {
  margin-bottom: 20px;
}

.confirm-content p {
  margin: 0 0 12px 0;
  color: #e6eef8;
  text-align: center;
  font-size: 14px;
}

.purchase-details {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 13px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #94a3b8;
}

.detail-value {
  color: #fbbf24;
  font-weight: bold;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.cancel-btn {
  padding: 8px 16px;
  border: 1px solid #6b7280;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  border-radius: 6px;
  font-size: 13px;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #6b7280;
  color: white;
}

.confirm-btn {
  padding: 8px 16px;
  border: 1px solid #16a34a;
  background: transparent;
  color: #16a34a;
  cursor: pointer;
  border-radius: 6px;
  font-size: 13px;
  transition: all 0.2s;
}

.confirm-btn:hover {
  background: #16a34a;
  color: white;
}

@media (max-width: 600px) {
  .treasure-card {
    min-width: 98vw;
    max-width: 98vw;
    height: 85vh;
  }

  .shop-item {
    min-height: 45px;
  }

  .item-description {
    -webkit-line-clamp: 1;
  }

  .confirm-card {
    min-width: 90vw;
    max-width: 90vw;
  }

  .confirm-actions {
    flex-direction: column;
  }

  .cancel-btn,
  .confirm-btn {
    width: 100%;
  }
}
</style>
